import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Tipos
interface Nivel {
  id: string;
  nome: string;
  xpNecessario: number;
  cor: string;
  icone: string;
  beneficios: string[];
}

interface Distintivo {
  id: string;
  nome: string;
  categoria: string;
  icone: string;
  descricao: string;
  conquistado: boolean;
  dataConquista?: string;
  raridade: 'comum' | 'raro' | 'épico' | 'lendário';
  requisitos: {
    xp?: number;
    nivel?: string;
    missao?: string;
    pontos?: number;
  };
}

interface Jogo {
  id: string;
  nome: string;
  categoria: string;
  icone: string;
  descricao: string;
  tipo: 'quiz' | 'memoria' | 'puzzle' | 'estrategia' | 'acao';
  dificuldade: 'facil' | 'medio' | 'dificil';
  xpRecompensa: number;
  pontosRecompensa: number;
  tempoLimite?: number;
  jogadas: number;
  melhorPontuacao: number;
  disponivel: boolean;
}

interface Missao {
  id: string;
  nome: string;
  categoria: string;
  icone: string;
  descricao: string;
  tipo: 'diaria' | 'semanal' | 'mensal' | 'unica';
  xpRecompensa: number;
  pontosRecompensa: number;
  progresso: number;
  meta: number;
  concluida: boolean;
  dataConclusao?: string;
  dataVencimento: string;
  dificuldade: 'facil' | 'medio' | 'dificil';
}

interface Recompensa {
  id: string;
  nome: string;
  categoria: string;
  descricao: string;
  imagem: string;
  pontosNecessarios: number;
  quantidadeDisponivel: number;
  tipo: 'produto' | 'experiencia' | 'desconto' | 'bonus';
  validade?: string;
  disponivel: boolean;
}

interface ParticipanteRanking {
  posicao: number;
  participante: {
    id: string;
    nome: string;
    avatar: string;
    nivel: string;
  };
  xp: number;
  pontos: number;
  distintivos: number;
  missoesCompletas: number;
}

interface ProgressaoCampanha {
  campanhaId: string;
  xpAtual: number;
  nivelAtual: Nivel;
  proximoNivel: Nivel | null;
  progressoNivel: number;
  distintivosConquistados: Distintivo[];
  totalDistintivos: number;
  missoesCompletas: number;
  totalMissoes: number;
  ranking: number;
  totalParticipantes: number;
  pontosDisponiveis: number;
  jogosDisponiveis: Jogo[];
  missoesAtivas: Missao[];
  recompensasDisponiveis: Recompensa[];
  rankingCompleto: ParticipanteRanking[];
}

interface Campanha {
  id: string;
  nome: string;
  descricao: string;
  status: 'ativa' | 'pausada' | 'finalizada' | 'pendente';
  dataInicio: string;
  dataFim: string;
  imagem: string;
  progresso: number;
  pontosGanhos: number;
  pontosDistribuidos: number;
  pontosDisponiveis: number;
  metaDefinida: number;
  ranking: number;
  totalParticipantes: number;
  progressao: ProgressaoCampanha;
  recursos?: {
    niveis?: boolean;
    distintivos?: boolean;
    missoes?: boolean;
    jogos?: boolean;
  };
  regulamento?: string;
  faq?: { pergunta: string; resposta: string }[];
}

interface Participante {
  id: string;
  nome: string;
  email: string;
  cpf: string;
  foto: string;
  cargo: string;
  departamento: string;
  pontosTotal: number;
  pontosDisponiveis: number;
  xpTotal: number;
  xpAtual: number;
  campanhasAtivas: number;
  missoesCompletas: number;
  resgatesRealizados: number;
  distintivosConquistados: number;
  notificacoesNaoLidas: number;
}

interface ParticipanteContextType {
  participante: Participante;
  campanhas: Campanha[];
  campanhaAtual: Campanha | null;
  setCampanhaAtual: (campanha: Campanha) => void;
  atualizarParticipante: () => void;
  confirmarParticipacao: (campanhaId: string) => void;
  todosDistintivos: Distintivo[];
}

const ParticipanteContext = createContext<ParticipanteContextType | undefined>(undefined);

export function ParticipanteProvider({ children }: { children: ReactNode }) {
  // Níveis padrão (podem ser customizados por campanha)
  const niveisBase: Nivel[] = [
    {
      id: '1',
      nome: 'Bronze',
      xpNecessario: 0,
      cor: '#CD7F32',
      icone: '🥉',
      beneficios: ['Acesso básico', 'Missões simples', 'Jogos básicos']
    },
    {
      id: '2',
      nome: 'Prata',
      xpNecessario: 1000,
      cor: '#C0C0C0',
      icone: '🥈',
      beneficios: ['Bônus 10% XP', 'Missões intermediárias', 'Mais jogos']
    },
    {
      id: '3',
      nome: 'Ouro',
      xpNecessario: 5000,
      cor: '#FFD700',
      icone: '🥇',
      beneficios: ['Bônus 20% XP', 'Missões avançadas', 'Prioridade suporte']
    },
    {
      id: '4',
      nome: 'Platina',
      xpNecessario: 15000,
      cor: '#E5E4E2',
      icone: '💎',
      beneficios: ['Bônus 30% XP', 'Missões exclusivas', 'Suporte VIP']
    },
    {
      id: '5',
      nome: 'Diamante',
      xpNecessario: 50000,
      cor: '#B9F2FF',
      icone: '💠',
      beneficios: ['Bônus 50% XP', 'Acesso total', 'Eventos exclusivos']
    }
  ];

  // Distintivos expandidos para todas as campanhas
  const distintivosCompletos: Distintivo[] = [
    // Distintivos Iniciante
    {
      id: 'dist1',
      nome: 'Primeiro Passo',
      categoria: 'Iniciante',
      icone: '👣',
      descricao: 'Complete sua primeira missão',
      conquistado: true,
      dataConquista: '2024-01-15',
      raridade: 'comum',
      requisitos: { xp: 0 }
    },
    {
      id: 'dist2',
      nome: 'Explorador',
      categoria: 'Iniciante',
      icone: '🗺️',
      descricao: 'Explore todas as seções da plataforma',
      conquistado: true,
      dataConquista: '2024-01-16',
      raridade: 'comum',
      requisitos: { xp: 100 }
    },
    {
      id: 'dist3',
      nome: 'Persistente',
      categoria: 'Iniciante',
      icone: '🎯',
      descricao: 'Complete 5 missões consecutivas',
      conquistado: true,
      dataConquista: '2024-01-20',
      raridade: 'comum',
      requisitos: { xp: 300 }
    },

    // Distintivos de Vendas
    {
      id: 'dist4',
      nome: 'Vendedor Estrela',
      categoria: 'Vendas',
      icone: '⭐',
      descricao: 'Realize 10 vendas em uma campanha',
      conquistado: true,
      dataConquista: '2024-02-10',
      raridade: 'raro',
      requisitos: { xp: 500, pontos: 1000 }
    },
    {
      id: 'dist5',
      nome: 'Mestre das Vendas',
      categoria: 'Vendas',
      icone: '👑',
      descricao: 'Atinja 100% da meta mensal',
      conquistado: false,
      raridade: 'épico',
      requisitos: { xp: 2000, pontos: 5000 }
    },
    {
      id: 'dist6',
      nome: 'Fenômeno de Vendas',
      categoria: 'Vendas',
      icone: '🚀',
      descricao: 'Supere a meta em 150%',
      conquistado: false,
      raridade: 'lendário',
      requisitos: { xp: 10000, pontos: 20000 }
    },

    // Distintivos de Clientes
    {
      id: 'dist7',
      nome: 'Cliente Fiel',
      categoria: 'Clientes',
      icone: '🤝',
      descricao: 'Ative 5 novos clientes',
      conquistado: false,
      raridade: 'raro',
      requisitos: { xp: 1000, nivel: 'Prata' }
    },
    {
      id: 'dist8',
      nome: 'Conquistador',
      categoria: 'Clientes',
      icone: '🏆',
      descricao: 'Ative 25 novos clientes',
      conquistado: false,
      raridade: 'épico',
      requisitos: { xp: 5000, nivel: 'Ouro' }
    },

    // Distintivos de Suporte
    {
      id: 'dist9',
      nome: 'Mestre do Suporte',
      categoria: 'Serviços',
      icone: '🎯',
      descricao: 'Complete 20 atendimentos com nota máxima',
      conquistado: false,
      raridade: 'épico',
      requisitos: { xp: 2000, nivel: 'Ouro' }
    },
    {
      id: 'dist10',
      nome: 'Solucionador',
      categoria: 'Serviços',
      icone: '🔧',
      descricao: 'Resolva 50 tickets de suporte',
      conquistado: false,
      raridade: 'raro',
      requisitos: { xp: 1500 }
    },

    // Distintivos Elite
    {
      id: 'dist11',
      nome: 'Elite',
      categoria: 'Elite',
      icone: '👑',
      descricao: 'Alcance o nível Diamante',
      conquistado: false,
      raridade: 'lendário',
      requisitos: { xp: 50000, nivel: 'Diamante' }
    },
    {
      id: 'dist12',
      nome: 'Lenda',
      categoria: 'Elite',
      icone: '🌟',
      descricao: 'Seja #1 em 3 campanhas diferentes',
      conquistado: false,
      raridade: 'lendário',
      requisitos: { xp: 100000 }
    },

    // Distintivos de Jogos
    {
      id: 'dist13',
      nome: 'Gamer',
      categoria: 'Jogos',
      icone: '🎮',
      descricao: 'Complete 10 jogos diferentes',
      conquistado: true,
      dataConquista: '2024-02-05',
      raridade: 'comum',
      requisitos: { xp: 200 }
    },
    {
      id: 'dist14',
      nome: 'Pro Player',
      categoria: 'Jogos',
      icone: '🏅',
      descricao: 'Obtenha pontuação máxima em 5 jogos',
      conquistado: false,
      raridade: 'épico',
      requisitos: { xp: 3000 }
    },

    // Distintivos Especiais
    {
      id: 'dist15',
      nome: 'Colaborativo',
      categoria: 'Social',
      icone: '🤗',
      descricao: 'Ajude 10 colegas em missões',
      conquistado: false,
      raridade: 'raro',
      requisitos: { xp: 800 }
    },
    {
      id: 'dist16',
      nome: 'Mentor',
      categoria: 'Social',
      icone: '🧑‍🏫',
      descricao: 'Treine 5 novos colaboradores',
      conquistado: false,
      raridade: 'épico',
      requisitos: { xp: 4000, nivel: 'Ouro' }
    }
  ];

  // Jogos expandidos para cada campanha
  const jogosCompletos: Jogo[] = [
    {
      id: 'jogo1',
      nome: 'Quiz de Produtos',
      categoria: 'Conhecimento',
      icone: '🧠',
      descricao: 'Teste seus conhecimentos sobre nossos produtos',
      tipo: 'quiz',
      dificuldade: 'medio',
      xpRecompensa: 150,
      pontosRecompensa: 50,
      tempoLimite: 300,
      jogadas: 12,
      melhorPontuacao: 850,
      disponivel: true
    },
    {
      id: 'jogo2',
      nome: 'Memória de Clientes',
      categoria: 'Memória',
      icone: '🎯',
      descricao: 'Memorize informações importantes dos clientes',
      tipo: 'memoria',
      dificuldade: 'facil',
      xpRecompensa: 100,
      pontosRecompensa: 30,
      tempoLimite: 180,
      jogadas: 8,
      melhorPontuacao: 720,
      disponivel: true
    },
    {
      id: 'jogo3',
      nome: 'Puzzle de Vendas',
      categoria: 'Estratégia',
      icone: '🧩',
      descricao: 'Monte a estratégia perfeita de vendas',
      tipo: 'puzzle',
      dificuldade: 'dificil',
      xpRecompensa: 250,
      pontosRecompensa: 100,
      tempoLimite: 600,
      jogadas: 5,
      melhorPontuacao: 950,
      disponivel: true
    },
    {
      id: 'jogo4',
      nome: 'Simulador de Atendimento',
      categoria: 'Simulação',
      icone: '📞',
      descricao: 'Pratique diferentes cenários de atendimento',
      tipo: 'estrategia',
      dificuldade: 'medio',
      xpRecompensa: 200,
      pontosRecompensa: 75,
      jogadas: 15,
      melhorPontuacao: 780,
      disponivel: true
    },
    {
      id: 'jogo5',
      nome: 'Corrida contra o Tempo',
      categoria: 'Ação',
      icone: '⚡',
      descricao: 'Complete tarefas no menor tempo possível',
      tipo: 'acao',
      dificuldade: 'dificil',
      xpRecompensa: 300,
      pontosRecompensa: 150,
      tempoLimite: 120,
      jogadas: 3,
      melhorPontuacao: 890,
      disponivel: true
    },
    {
      id: 'jogo6',
      nome: 'Caça ao Tesouro',
      categoria: 'Aventura',
      icone: '🗺️',
      descricao: 'Encontre pistas escondidas pela empresa',
      tipo: 'estrategia',
      dificuldade: 'medio',
      xpRecompensa: 180,
      pontosRecompensa: 60,
      jogadas: 7,
      melhorPontuacao: 650,
      disponivel: true
    }
  ];

  // Missões expandidas para cada campanha
  const missoesCompletas: Missao[] = [
    // Missões Diárias
    {
      id: 'missao1',
      nome: 'Check-in Diário',
      categoria: 'Rotina',
      icone: '✅',
      descricao: 'Faça login na plataforma',
      tipo: 'diaria',
      xpRecompensa: 50,
      pontosRecompensa: 10,
      progresso: 1,
      meta: 1,
      concluida: true,
      dataConclusao: '2024-01-15',
      dataVencimento: '2024-01-15',
      dificuldade: 'facil'
    },
    {
      id: 'missao2',
      nome: 'Meta Diária de Vendas',
      categoria: 'Vendas',
      icone: '💰',
      descricao: 'Realize 3 vendas hoje',
      tipo: 'diaria',
      xpRecompensa: 120,
      pontosRecompensa: 40,
      progresso: 2,
      meta: 3,
      concluida: false,
      dataVencimento: '2024-01-15',
      dificuldade: 'medio'
    },
    {
      id: 'missao3',
      nome: 'Atendimentos de Qualidade',
      categoria: 'Suporte',
      icone: '⭐',
      descricao: 'Complete 5 atendimentos com nota 5',
      tipo: 'diaria',
      xpRecompensa: 100,
      pontosRecompensa: 30,
      progresso: 3,
      meta: 5,
      concluida: false,
      dataVencimento: '2024-01-15',
      dificuldade: 'medio'
    },

    // Missões Semanais
    {
      id: 'missao4',
      nome: 'Conquistador Semanal',
      categoria: 'Clientes',
      icone: '🎯',
      descricao: 'Ative 5 novos clientes esta semana',
      tipo: 'semanal',
      xpRecompensa: 300,
      pontosRecompensa: 150,
      progresso: 3,
      meta: 5,
      concluida: false,
      dataVencimento: '2024-01-21',
      dificuldade: 'medio'
    },
    {
      id: 'missao5',
      nome: 'Jogador Dedicado',
      categoria: 'Jogos',
      icone: '🎮',
      descricao: 'Complete 10 jogos diferentes',
      tipo: 'semanal',
      xpRecompensa: 250,
      pontosRecompensa: 100,
      progresso: 7,
      meta: 10,
      concluida: false,
      dataVencimento: '2024-01-21',
      dificuldade: 'facil'
    },
    {
      id: 'missao6',
      nome: 'Social Butterfly',
      categoria: 'Social',
      icone: '🤝',
      descricao: 'Interaja com 15 colegas na plataforma',
      tipo: 'semanal',
      xpRecompensa: 200,
      pontosRecompensa: 80,
      progresso: 12,
      meta: 15,
      concluida: false,
      dataVencimento: '2024-01-21',
      dificuldade: 'facil'
    },

    // Missões Mensais
    {
      id: 'missao7',
      nome: 'Mestre do Mês',
      categoria: 'Elite',
      icone: '👑',
      descricao: 'Fique entre os top 3 do ranking',
      tipo: 'mensal',
      xpRecompensa: 800,
      pontosRecompensa: 500,
      progresso: 1,
      meta: 1,
      concluida: true,
      dataConclusao: '2024-01-31',
      dataVencimento: '2024-01-31',
      dificuldade: 'dificil'
    },
    {
      id: 'missao8',
      nome: 'Vendedor Fenomenal',
      categoria: 'Vendas',
      icone: '🚀',
      descricao: 'Supere sua meta mensal em 120%',
      tipo: 'mensal',
      xpRecompensa: 600,
      pontosRecompensa: 400,
      progresso: 85,
      meta: 120,
      concluida: false,
      dataVencimento: '2024-01-31',
      dificuldade: 'dificil'
    },
    {
      id: 'missao9',
      nome: 'Colecionador',
      categoria: 'Distintivos',
      icone: '🏆',
      descricao: 'Conquiste 5 distintivos diferentes',
      tipo: 'mensal',
      xpRecompensa: 400,
      pontosRecompensa: 300,
      progresso: 2,
      meta: 5,
      concluida: false,
      dataVencimento: '2024-01-31',
      dificuldade: 'medio'
    },

    // Missões Únicas
    {
      id: 'missao10',
      nome: 'Bem-vindo!',
      categoria: 'Onboarding',
      icone: '🎉',
      descricao: 'Complete o processo de onboarding',
      tipo: 'unica',
      xpRecompensa: 500,
      pontosRecompensa: 200,
      progresso: 1,
      meta: 1,
      concluida: true,
      dataConclusao: '2024-01-10',
      dataVencimento: '2024-12-31',
      dificuldade: 'facil'
    },
    {
      id: 'missao11',
      nome: 'Primeiro Milhão',
      categoria: 'Marco',
      icone: '💎',
      descricao: 'Acumule 1 milhão em vendas',
      tipo: 'unica',
      xpRecompensa: 2000,
      pontosRecompensa: 1000,
      progresso: 650000,
      meta: 1000000,
      concluida: false,
      dataVencimento: '2024-12-31',
      dificuldade: 'dificil'
    },
    {
      id: 'missao12',
      nome: 'Cliente VIP',
      categoria: 'Relacionamento',
      icone: '💼',
      descricao: 'Converta um cliente para o plano VIP',
      tipo: 'unica',
      xpRecompensa: 1000,
      pontosRecompensa: 600,
      progresso: 0,
      meta: 1,
      concluida: false,
      dataVencimento: '2024-12-31',
      dificuldade: 'dificil'
    }
  ];

  // Recompensas expandidas
  const recompensasCompletas: Recompensa[] = [
    // Produtos Eletrônicos
    {
      id: 'rec1',
      nome: 'iPhone 15 Pro',
      categoria: 'Eletrônicos',
      descricao: 'Smartphone Apple iPhone 15 Pro 128GB',
      imagem: 'https://readdy.ai/api/search-image?query=iphone%2015%20pro%20smartphone%20apple%20sleek%20design%20professional%20product%20photography%20white%20background%20modern%20technology&width=300&height=200&seq=rec1&orientation=landscape',
      pontosNecessarios: 25000,
      quantidadeDisponivel: 3,
      tipo: 'produto',
      validade: '2024-12-31',
      disponivel: true
    },
    {
      id: 'rec2',
      nome: 'MacBook Air M2',
      categoria: 'Eletrônicos',
      descricao: 'Notebook Apple MacBook Air 13" M2 256GB',
      imagem: 'https://readdy.ai/api/search-image?query=macbook%20air%20m2%20laptop%20apple%20silver%20design%20professional%20product%20photography%20clean%20background%20modern%20computer&width=300&height=200&seq=rec2&orientation=landscape',
      pontosNecessarios: 45000,
      quantidadeDisponivel: 1,
      tipo: 'produto',
      validade: '2024-12-31',
      disponivel: false
    },
    {
      id: 'rec3',
      nome: 'AirPods Pro',
      categoria: 'Eletrônicos',
      descricao: 'Fones Apple AirPods Pro 2ª Geração',
      imagem: 'https://readdy.ai/api/search-image?query=airpods%20pro%20apple%20wireless%20earbuds%20white%20design%20professional%20product%20photography%20clean%20background%20modern%20audio&width=300&height=200&seq=rec3&orientation=landscape',
      pontosNecessarios: 8500,
      quantidadeDisponivel: 10,
      tipo: 'produto',
      validade: '2024-12-31',
      disponivel: true
    },

    // Experiências
    {
      id: 'rec4',
      nome: 'Viagem para Cancún',
      categoria: 'Viagem',
      descricao: 'Pacote completo para 2 pessoas - 5 dias',
      imagem: 'https://readdy.ai/api/search-image?query=cancun%20beach%20vacation%20tropical%20paradise%20crystal%20clear%20water%20white%20sand%20palm%20trees%20luxury%20resort%20travel%20destination&width=300&height=200&seq=rec4&orientation=landscape',
      pontosNecessarios: 50000,
      quantidadeDisponivel: 2,
      tipo: 'experiencia',
      validade: '2024-06-30',
      disponivel: true
    },
    {
      id: 'rec5',
      nome: 'Jantar no Fasano',
      categoria: 'Gastronomia',
      descricao: 'Jantar para 2 pessoas no restaurante Fasano',
      imagem: 'https://readdy.ai/api/search-image?query=fine%20dining%20restaurant%20elegant%20atmosphere%20gourmet%20food%20luxury%20interior%20sophisticated%20culinary%20experience%20upscale%20dining&width=300&height=200&seq=rec5&orientation=landscape',
      pontosNecessarios: 3500,
      quantidadeDisponivel: 8,
      tipo: 'experiencia',
      validade: '2024-03-31',
      disponivel: true
    },

    // Descontos
    {
      id: 'rec6',
      nome: 'Vale-Compras R$ 500',
      categoria: 'Vale-Compras',
      descricao: 'Vale-compras de R$ 500 em lojas parceiras',
      imagem: 'https://readdy.ai/api/search-image?query=gift%20card%20shopping%20voucher%20500%20reais%20brazilian%20currency%20elegant%20design%20professional%20modern%20clean%20background&width=300&height=200&seq=rec6&orientation=landscape',
      pontosNecessarios: 4000,
      quantidadeDisponivel: 15,
      tipo: 'desconto',
      validade: '2024-12-31',
      disponivel: true
    },
    {
      id: 'rec7',
      nome: 'Desconto 50% Academia',
      categoria: 'Saúde',
      descricao: 'Desconto de 50% na mensalidade da academia',
      imagem: 'https://readdy.ai/api/search-image?query=gym%20fitness%20center%20modern%20equipment%20healthy%20lifestyle%20workout%20training%20professional%20clean%20environment%20wellness&width=300&height=200&seq=rec7&orientation=landscape',
      pontosNecessarios: 1200,
      quantidadeDisponivel: 20,
      tipo: 'desconto',
      validade: '2024-04-30',
      disponivel: true
    },

    // Bônus
    {
      id: 'rec8',
      nome: 'Bônus 2x XP',
      categoria: 'Bônus',
      descricao: 'Ganhe XP em dobro por 30 dias',
      imagem: 'https://readdy.ai/api/search-image?query=bonus%20multiplier%202x%20experience%20points%20golden%20trophy%20achievement%20reward%20professional%20gaming%20elements&width=300&height=200&seq=rec8&orientation=landscape',
      pontosNecessarios: 2000,
      quantidadeDisponivel: 999,
      tipo: 'bonus',
      validade: '2024-12-31',
      disponivel: true
    },
    {
      id: 'rec9',
      nome: 'Folga Extra',
      categoria: 'Tempo',
      descricao: '1 dia de folga extra remunerada',
      imagem: 'https://readdy.ai/api/search-image?query=extra%20day%20off%20vacation%20relaxation%20beach%20hammock%20peaceful%20rest%20reward%20work%20life%20balance%20professional&width=300&height=200&seq=rec9&orientation=landscape',
      pontosNecessarios: 5000,
      quantidadeDisponivel: 5,
      tipo: 'bonus',
      validade: '2024-12-31',
      disponivel: true
    },

    // Produtos Variados
    {
      id: 'rec10',
      nome: 'Smart Watch',
      categoria: 'Eletrônicos',
      descricao: 'Relógio inteligente com GPS e monitor cardíaco',
      imagem: 'https://readdy.ai/api/search-image?query=smart%20watch%20fitness%20tracker%20modern%20design%20black%20sleek%20professional%20product%20photography%20clean%20background%20wearable%20technology&width=300&height=200&seq=rec10&orientation=landscape',
      pontosNecessarios: 6500,
      quantidadeDisponivel: 7,
      tipo: 'produto',
      validade: '2024-12-31',
      disponivel: true
    }
  ];

  // Ranking completo por campanha
  const gerarRanking = (totalParticipantes: number, posicaoUsuario: number): ParticipanteRanking[] => {
    const nomes = [
      'Ana Silva', 'Bruno Santos', 'Carlos Silva', 'Diana Costa', 'Eduardo Lima',
      'Fernanda Oliveira', 'Gabriel Rodrigues', 'Helena Martins', 'Igor Pereira', 'Juliana Alves',
      'Kevin Souza', 'Larissa Ferreira', 'Marcos Ribeiro', 'Natália Campos', 'Otávio Barbosa',
      'Patricia Gomes', 'Quintino Dias', 'Rafaela Cruz', 'Samuel Torres', 'Tatiana Vieira',
      'Ulisses Melo', 'Vanessa Cardoso', 'Wesley Nascimento', 'Ximena Ramos', 'Yuri Andrade',
      'Zélia Monteiro', 'Alberto Farias', 'Beatriz Rocha', 'Caio Mendes', 'Débora Teixeira'
    ];

    const avatars = Array.from({length: 30}, (_, i) => `https://i.pravatar.cc/150?img=${i + 1}`);

    return Array.from({length: totalParticipantes}, (_, index) => {
      const posicao = index + 1;
      const isUsuario = posicao === posicaoUsuario;
      
      return {
        posicao,
        participante: {
          id: `user_${index + 1}`,
          nome: isUsuario ? 'Carlos Silva' : nomes[index % nomes.length],
          avatar: isUsuario ? 'https://i.pravatar.cc/150?img=12' : avatars[index % avatars.length],
          nivel: posicao <= 5 ? 'Diamante' : posicao <= 15 ? 'Platina' : posicao <= 30 ? 'Ouro' : 'Prata'
        },
        xp: Math.max(100, 50000 - (posicao * 800) + Math.floor(Math.random() * 500)),
        pontos: Math.max(50, 25000 - (posicao * 400) + Math.floor(Math.random() * 300)),
        distintivos: Math.max(1, Math.floor(Math.random() * 8) + (posicao <= 10 ? 3 : 1)),
        missoesCompletas: Math.max(1, Math.floor(Math.random() * 20) + (posicao <= 5 ? 15 : 5))
      };
    });
  };

  const calcularNivelAtual = (xp: number, niveis: Nivel[]): Nivel => {
    const niveisOrdenados = [...niveis].sort((a, b) => b.xpNecessario - a.xpNecessario);
    return niveisOrdenados.find(n => xp >= n.xpNecessario) || niveis[0];
  };

  const calcularProximoNivel = (nivelAtual: Nivel, niveis: Nivel[]): Nivel | null => {
    const niveisOrdenados = [...niveis].sort((a, b) => a.xpNecessario - b.xpNecessario);
    const indexAtual = niveisOrdenados.findIndex(n => n.id === nivelAtual.id);
    return indexAtual < niveisOrdenados.length - 1 ? niveisOrdenados[indexAtual + 1] : null;
  };

  const calcularProgressoNivel = (xpAtual: number, nivelAtual: Nivel, proximoNivel: Nivel | null): number => {
    if (!proximoNivel) return 100;
    const xpBase = nivelAtual.xpNecessario;
    const xpNecessario = proximoNivel.xpNecessario - xpBase;
    const xpProgresso = xpAtual - xpBase;
    return Math.min(100, Math.max(0, (xpProgresso / xpNecessario) * 100));
  };

  // Campanhas mockadas com massa de dados completa
  const [campanhas, setCampanhas] = useState<Campanha[]>([
    {
      id: '0',
      nome: 'Campanha Primavera 2024',
      descricao: 'Nova campanha de vendas com metas desafiadoras e prêmios incríveis',
      status: 'pendente',
      dataInicio: '2024-04-01',
      dataFim: '2024-06-30',
      imagem: 'https://readdy.ai/api/search-image?query=spring%20sales%20campaign%20blooming%20flowers%20fresh%20start%20new%20beginning%20professional%20business%20background%20with%20vibrant%20colors%20and%20growth%20symbols%20modern%20corporate%20atmosphere&width=800&height=400&seq=camp0&orientation=landscape',
      progresso: 0,
      pontosGanhos: 0,
      pontosDistribuidos: 0,
      pontosDisponiveis: 0,
      metaDefinida: 8000,
      ranking: 0,
      totalParticipantes: 0,
      recursos: {
        niveis: true,
        distintivos: true,
        missoes: true,
        jogos: true
      },
      regulamento: `REGULAMENTO DA CAMPANHA PRIMAVERA 2024

1. OBJETIVO DA CAMPANHA
Esta campanha tem como objetivo incentivar o aumento de vendas e o engajamento dos participantes durante o período da primavera de 2024, oferecendo recompensas e reconhecimento aos melhores desempenhos.

2. PERÍODO DE VIGÊNCIA
A campanha terá início em 01/04/2024 e término em 30/06/2024, totalizando 90 dias de duração.

3. PARTICIPANTES ELEGÍVEIS
Poderão participar todos os colaboradores ativos da empresa que estejam devidamente cadastrados na plataforma e que aceitem os termos deste regulamento.

4. MECÂNICA DA CAMPANHA
4.1. Os participantes acumularão pontos através da realização de vendas, conclusão de missões e participação em jogos disponíveis na plataforma.
4.2. Cada venda realizada gerará pontos de acordo com a tabela de pontuação estabelecida pela empresa.
4.3. Missões diárias, semanais e mensais estarão disponíveis para acúmulo adicional de pontos.
4.4. A pontuação será atualizada em tempo real na plataforma.

5. SISTEMA DE PONTUAÇÃO
5.1. Vendas: De 10 a 500 pontos por venda, dependendo do valor e tipo de produto.
5.2. Missões Diárias: 10 a 50 pontos por missão concluída.
5.3. Missões Semanais: 100 a 300 pontos por missão concluída.
5.4. Missões Mensais: 500 a 1000 pontos por missão concluída.
5.5. Jogos: 30 a 150 pontos por jogo concluído com sucesso.

6. PREMIAÇÃO
6.1. Os participantes poderão resgatar seus pontos por recompensas disponíveis no catálogo da plataforma.
6.2. Prêmios especiais serão concedidos aos 3 primeiros colocados no ranking geral ao final da campanha.
6.3. Distintivos especiais serão concedidos aos participantes que atingirem marcos específicos.

7. RANKING E CLASSIFICAÇÃO
7.1. O ranking será atualizado diariamente com base na pontuação acumulada.
7.2. Em caso de empate, será considerado o participante que atingiu a pontuação primeiro.
7.3. O ranking será público e visível para todos os participantes.

8. REGRAS GERAIS
8.1. É proibida qualquer forma de fraude ou manipulação de resultados.
8.2. A empresa se reserva o direito de desclassificar participantes que violarem as regras.
8.3. Os pontos não são transferíveis entre participantes.
8.4. A empresa pode alterar ou encerrar a campanha a qualquer momento, mediante comunicação prévia.

9. ACEITE E CONFIRMAÇÃO
9.1. Para participar da campanha, o colaborador deve ler e aceitar este regulamento.
9.2. A confirmação de participação será feita através de código de verificação enviado por e-mail.
9.3. O código de verificação tem validade de 24 horas.

10. DISPOSIÇÕES FINAIS
10.1. Os casos omissos serão resolvidos pela comissão organizadora da campanha.
10.2. Dúvidas podem ser esclarecidas através do suporte da plataforma.
10.3. A participação na campanha implica na aceitação integral deste regulamento.

Data de publicação: 15/03/2024
Última atualização: 15/03/2024`,
      progressao: {
        campanhaId: '0',
        xpAtual: 0,
        nivelAtual: calcularNivelAtual(0, niveisBase),
        proximoNivel: calcularProximoNivel(calcularNivelAtual(0, niveisBase), niveisBase),
        progressoNivel: 0,
        distintivosConquistados: [],
        totalDistintivos: distintivosCompletos.length,
        missoesCompletas: 0,
        totalMissoes: 0,
        ranking: 0,
        totalParticipantes: 0,
        pontosDisponiveis: 0,
        jogosDisponiveis: [],
        missoesAtivas: [],
        recompensasDisponiveis: [],
        rankingCompleto: []
      }
    },
    {
      id: '1',
      nome: 'Campanha Verão 2024',
      descricao: 'Aumente suas vendas neste verão e alcance novos patamares',
      status: 'ativa',
      dataInicio: '2024-01-01',
      dataFim: '2024-03-31',
      imagem: 'https://readdy.ai/api/search-image?query=summer%20sales%20campaign%20tropical%20beach%20sunset%20vibrant%20colors%20professional%20marketing%20background%20with%20palm%20trees%20and%20ocean%20waves%20modern%20business%20atmosphere&width=800&height=400&seq=camp1&orientation=landscape',
      progresso: 65,
      pontosGanhos: 3250,
      pontosDistribuidos: 5000,
      pontosDisponiveis: 1750,
      metaDefinida: 5000,
      ranking: 3,
      totalParticipantes: 45,
      recursos: {
        niveis: true,
        distintivos: true,
        missoes: true,
        jogos: true
      },
      regulamento: `REGULAMENTO DA CAMPANHA VERÃO 2024

1. OBJETIVO
Incentivar o aumento de vendas e engajamento dos participantes durante o verão de 2024.

2. PERÍODO DE VIGÊNCIA
01/01/2024 a 31/03/2024.

3. PARTICIPANTES ELEGÍVEIS
Todos os colaboradores ativos devidamente cadastrados na plataforma.

4. MECÂNICA
Os participantes acumulam pontos por vendas realizadas, missões concluídas e jogos completados. A pontuação é atualizada em tempo real.

5. PONTUAÇÃO
- Vendas: 10 a 500 pontos por venda
- Missões Diárias: 10 a 50 pontos
- Missões Semanais: 100 a 300 pontos
- Missões Mensais: 500 a 1.000 pontos
- Jogos: 30 a 150 pontos

6. PREMIAÇÃO
Pontos podem ser trocados por recompensas no catálogo. Os 3 primeiros do ranking recebem prêmios especiais ao final da campanha.

7. REGRAS GERAIS
É proibida qualquer forma de fraude. A empresa pode desclassificar participantes que violarem as regras. Pontos não são transferíveis.`,
      faq: [
        { pergunta: 'Como acumulo pontos nesta campanha?', resposta: 'Você acumula pontos realizando vendas, completando missões diárias, semanais e mensais, e participando dos jogos disponíveis na plataforma.' },
        { pergunta: 'Quando os pontos são creditados?', resposta: 'Os pontos são creditados automaticamente após a confirmação de cada venda ou conclusão de missão, em tempo real.' },
        { pergunta: 'Posso resgatar recompensas durante a campanha?', resposta: 'Sim! Você pode resgatar recompensas a qualquer momento durante a campanha, desde que tenha pontos suficientes disponíveis.' },
        { pergunta: 'O que acontece com meus pontos ao final da campanha?', resposta: 'Os pontos acumulados ficam disponíveis para resgate por 90 dias após o encerramento da campanha.' },
        { pergunta: 'Como funciona o ranking?', resposta: 'O ranking é atualizado diariamente com base no XP acumulado. Em caso de empate, vence quem atingiu a pontuação primeiro.' },
        { pergunta: 'Posso participar de mais de uma campanha ao mesmo tempo?', resposta: 'Sim, você pode participar de todas as campanhas ativas simultaneamente. Os pontos e XP são contabilizados separadamente para cada campanha.' },
      ],
      progressao: {
        campanhaId: '1',
        xpAtual: 3200,
        nivelAtual: calcularNivelAtual(3200, niveisBase),
        proximoNivel: calcularProximoNivel(calcularNivelAtual(3200, niveisBase), niveisBase),
        progressoNivel: calcularProgressoNivel(3200, calcularNivelAtual(3200, niveisBase), calcularProximoNivel(calcularNivelAtual(3200, niveisBase), niveisBase)),
        distintivosConquistados: distintivosCompletos.filter(d => d.conquistado),
        totalDistintivos: distintivosCompletos.length,
        missoesCompletas: 8,
        totalMissoes: 12,
        ranking: 3,
        totalParticipantes: 45,
        pontosDisponiveis: 1750,
        jogosDisponiveis: jogosCompletos,
        missoesAtivas: missoesCompletas,
        recompensasDisponiveis: recompensasCompletas,
        rankingCompleto: gerarRanking(45, 3)
      }
    },
    {
      id: '2',
      nome: 'Mega Vendas Q1',
      descricao: 'Bata todas as metas do primeiro trimestre e ganhe recompensas exclusivas',
      status: 'ativa',
      dataInicio: '2024-01-01',
      dataFim: '2024-03-31',
      imagem: 'https://readdy.ai/api/search-image?query=mega%20sales%20first%20quarter%20business%20growth%20chart%20upward%20trend%20professional%20corporate%20background%20with%20graphs%20and%20success%20indicators&width=800&height=400&seq=camp2&orientation=landscape',
      progresso: 42,
      pontosGanhos: 2100,
      pontosDistribuidos: 5000,
      pontosDisponiveis: 2900,
      metaDefinida: 5000,
      ranking: 7,
      totalParticipantes: 38,
      recursos: {
        niveis: true,
        distintivos: true,
        missoes: true,
        jogos: false
      },
      regulamento: `REGULAMENTO DA CAMPANHA MEGA VENDAS Q1

1. OBJETIVO
Maximizar as vendas no primeiro trimestre de 2024 com metas desafiadoras e recompensas exclusivas.

2. PERÍODO DE VIGÊNCIA
01/01/2024 a 31/03/2024.

3. PARTICIPANTES ELEGÍVEIS
Todos os colaboradores ativos da equipe comercial cadastrados na plataforma.

4. MECÂNICA
Pontos são acumulados por vendas e missões. Não há jogos nesta campanha.

5. PONTUAÇÃO
- Vendas: 15 a 600 pontos por venda (bônus Q1)
- Missões Diárias: 10 a 50 pontos
- Missões Semanais: 100 a 300 pontos
- Missões Mensais: 500 a 1.000 pontos

6. PREMIAÇÃO
Top 3 do ranking recebem prêmios especiais. Todos os participantes que atingirem 100% da meta recebem bônus adicional.

7. REGRAS GERAIS
Fraudes resultam em desclassificação imediata. Pontos não são transferíveis entre participantes.`,
      faq: [
        { pergunta: 'Por que não há jogos nesta campanha?', resposta: 'A Mega Vendas Q1 é focada exclusivamente em performance de vendas e missões, sem módulo de jogos para manter o foco nos resultados comerciais.' },
        { pergunta: 'Existe bônus por superar a meta?', resposta: 'Sim! Participantes que superarem 120% da meta recebem multiplicador de 1,5x nos pontos das vendas excedentes.' },
        { pergunta: 'Como são calculados os pontos de venda?', resposta: 'Os pontos variam conforme o valor e tipo de produto vendido. Produtos premium geram mais pontos. Consulte a tabela de pontuação na área de objetivos.' },
        { pergunta: 'Posso ver o progresso dos meus colegas?', resposta: 'Sim, o ranking é público e atualizado diariamente. Você pode acompanhar sua posição em relação aos demais participantes.' },
        { pergunta: 'O que acontece se eu faltar durante a campanha?', resposta: 'Missões diárias expiram ao final do dia. Missões semanais e mensais permanecem disponíveis durante todo o período estipulado.' },
      ],
      progressao: {
        campanhaId: '2',
        xpAtual: 1800,
        nivelAtual: calcularNivelAtual(1800, niveisBase),
        proximoNivel: calcularProximoNivel(calcularNivelAtual(1800, niveisBase), niveisBase),
        progressoNivel: calcularProgressoNivel(1800, calcularNivelAtual(1800, niveisBase), calcularProximoNivel(calcularNivelAtual(1800, niveisBase), niveisBase)),
        distintivosConquistados: distintivosCompletos.filter(d => d.conquistado).slice(0, 1),
        totalDistintivos: distintivosCompletos.length,
        missoesCompletas: 5,
        totalMissoes: 15,
        ranking: 7,
        totalParticipantes: 38,
        pontosDisponiveis: 2900,
        jogosDisponiveis: jogosCompletos.slice(0, 3),
        missoesAtivas: missoesCompletas.slice(0, 8),
        recompensasDisponiveis: recompensasCompletas.slice(0, 6),
        rankingCompleto: gerarRanking(38, 7)
      }
    },
    {
      id: '3',
      nome: 'Ativação de Clientes',
      descricao: 'Traga novos clientes para a base e expanda nosso alcance',
      status: 'ativa',
      dataInicio: '2024-02-01',
      dataFim: '2024-04-30',
      imagem: 'https://readdy.ai/api/search-image?query=customer%20activation%20campaign%20new%20clients%20handshake%20partnership%20professional%20business%20background%20with%20people%20networking%20and%20collaboration&width=800&height=400&seq=camp3&orientation=landscape',
      progresso: 78,
      pontosGanhos: 4680,
      pontosDistribuidos: 6000,
      pontosDisponiveis: 1320,
      metaDefinida: 6000,
      ranking: 2,
      totalParticipantes: 52,
      recursos: {
        niveis: true,
        distintivos: true,
        missoes: true,
        jogos: true
      },
      regulamento: `REGULAMENTO DA CAMPANHA ATIVAÇÃO DE CLIENTES

1. OBJETIVO
Expandir a base de clientes ativos e fortalecer relacionamentos comerciais durante o período de fevereiro a abril de 2024.

2. PERÍODO DE VIGÊNCIA
01/02/2024 a 30/04/2024.

3. PARTICIPANTES ELEGÍVEIS
Colaboradores das áreas comercial e de relacionamento com clientes.

4. MECÂNICA
Pontos são acumulados por ativação de novos clientes, missões e jogos. Cada novo cliente ativado gera pontuação especial.

5. PONTUAÇÃO
- Ativação de cliente novo: 200 a 800 pontos
- Missões Diárias: 10 a 50 pontos
- Missões Semanais: 100 a 300 pontos
- Missões Mensais: 500 a 1.000 pontos
- Jogos: 30 a 150 pontos

6. PREMIAÇÃO
Bônus especial para quem ativar mais de 10 clientes novos. Top 3 recebem prêmios exclusivos.

7. REGRAS GERAIS
Clientes devem ser genuinamente novos na base. Reativações contam com pontuação reduzida (50%).`,
      faq: [
        { pergunta: 'O que conta como "novo cliente"?', resposta: 'Clientes que nunca tiveram relacionamento comercial com a empresa ou que estão inativos há mais de 12 meses.' },
        { pergunta: 'Reativações de clientes inativos contam?', resposta: 'Sim, mas com pontuação reduzida de 50% em relação à ativação de um cliente completamente novo.' },
        { pergunta: 'Há limite de clientes que posso ativar?', resposta: 'Não há limite! Quanto mais clientes você ativar, mais pontos acumula. Há bônus progressivo a partir do 10º cliente.' },
        { pergunta: 'Quando os pontos de ativação são creditados?', resposta: 'Os pontos são creditados após a confirmação do cadastro do cliente no sistema, geralmente em até 24 horas.' },
        { pergunta: 'Posso ativar clientes de outras regiões?', resposta: 'Sim, desde que esteja dentro das diretrizes comerciais da empresa. Consulte seu gestor para casos específicos.' },
      ],
      progressao: {
        campanhaId: '3',
        xpAtual: 6500,
        nivelAtual: calcularNivelAtual(6500, niveisBase),
        proximoNivel: calcularProximoNivel(calcularNivelAtual(6500, niveisBase), niveisBase),
        progressoNivel: calcularProgressoNivel(6500, calcularNivelAtual(6500, niveisBase), calcularProximoNivel(calcularNivelAtual(6500, niveisBase), niveisBase)),
        distintivosConquistados: distintivosCompletos.filter(d => d.conquistado),
        totalDistintivos: distintivosCompletos.length,
        missoesCompletas: 11,
        totalMissoes: 14,
        ranking: 2,
        totalParticipantes: 52,
        pontosDisponiveis: 1320,
        jogosDisponiveis: jogosCompletos,
        missoesAtivas: missoesCompletas,
        recompensasDisponiveis: recompensasCompletas,
        rankingCompleto: gerarRanking(52, 2)
      }
    },
    {
      id: '4',
      nome: 'Excelência em Atendimento',
      descricao: 'Supere as expectativas dos clientes e seja reconhecido pela qualidade',
      status: 'ativa',
      dataInicio: '2024-01-15',
      dataFim: '2024-06-30',
      imagem: 'https://readdy.ai/api/search-image?query=customer%20service%20excellence%20support%20team%20professional%20office%20environment%20with%20happy%20clients%20and%20quality%20service%20indicators%20modern%20workplace&width=800&height=400&seq=camp4&orientation=landscape',
      progresso: 88,
      pontosGanhos: 8800,
      pontosDistribuidos: 10000,
      pontosDisponiveis: 1200,
      metaDefinida: 10000,
      ranking: 1,
      totalParticipantes: 62,
      recursos: {
        niveis: true,
        distintivos: true,
        missoes: true,
        jogos: true
      },
      regulamento: `REGULAMENTO DA CAMPANHA EXCELÊNCIA EM ATENDIMENTO

1. OBJETIVO
Elevar o padrão de qualidade no atendimento ao cliente, reconhecendo e recompensando os colaboradores com melhor desempenho.

2. PERÍODO DE VIGÊNCIA
15/01/2024 a 30/06/2024.

3. PARTICIPANTES ELEGÍVEIS
Todos os colaboradores das áreas de atendimento, suporte e relacionamento com clientes.

4. MECÂNICA
Pontos são acumulados com base nas avaliações dos clientes, tempo de resolução e missões concluídas.

5. PONTUAÇÃO
- Avaliação 5 estrelas: 100 pontos
- Avaliação 4 estrelas: 60 pontos
- Resolução no primeiro contato: bônus de 50 pontos
- Missões: conforme tabela de missões
- Jogos: 30 a 150 pontos

6. PREMIAÇÃO
O colaborador com maior NPS ao final da campanha recebe prêmio especial. Top 3 do ranking geral recebem recompensas exclusivas.

7. REGRAS GERAIS
Avaliações suspeitas ou manipuladas serão desconsideradas. O NPS mínimo para participar do ranking final é 7.0.`,
      faq: [
        { pergunta: 'Como é calculado o NPS da campanha?', resposta: 'O NPS é calculado com base nas avaliações dos clientes atendidos. Notas 9-10 são promotores, 7-8 neutros e 0-6 detratores. A fórmula é: % promotores - % detratores.' },
        { pergunta: 'Avaliações negativas afetam minha pontuação?', resposta: 'Avaliações abaixo de 4 estrelas não geram pontos, mas não subtraem pontos já acumulados. Foque em manter alta qualidade!' },
        { pergunta: 'Quantas avaliações preciso ter para aparecer no ranking?', resposta: 'É necessário ter no mínimo 10 avaliações para aparecer no ranking oficial da campanha.' },
        { pergunta: 'O que é "resolução no primeiro contato"?', resposta: 'Quando o problema do cliente é resolvido completamente na primeira interação, sem necessidade de retorno ou escalonamento.' },
        { pergunta: 'Posso ver as avaliações que recebi?', resposta: 'Sim! Todas as avaliações ficam disponíveis no seu painel de desempenho, com comentários dos clientes quando fornecidos.' },
        { pergunta: 'Há penalidade por tempo de resposta alto?', resposta: 'Não há penalidade direta, mas atendimentos resolvidos rapidamente geram bônus de pontos. O tempo ideal é abaixo de 2 horas.' },
      ],
      progressao: {
        campanhaId: '4',
        xpAtual: 18500,
        nivelAtual: calcularNivelAtual(18500, niveisBase),
        proximoNivel: calcularProximoNivel(calcularNivelAtual(18500, niveisBase), niveisBase),
        progressoNivel: calcularProgressoNivel(18500, calcularNivelAtual(18500, niveisBase), calcularProximoNivel(calcularNivelAtual(18500, niveisBase), niveisBase)),
        distintivosConquistados: distintivosCompletos.filter(d => d.conquistado),
        totalDistintivos: distintivosCompletos.length,
        missoesCompletas: 18,
        totalMissoes: 20,
        ranking: 1,
        totalParticipantes: 62,
        pontosDisponiveis: 1200,
        jogosDisponiveis: jogosCompletos,
        missoesAtivas: missoesCompletas,
        recompensasDisponiveis: recompensasCompletas,
        rankingCompleto: gerarRanking(62, 1)
      }
    },
    {
      id: '5',
      nome: 'Inovação e Crescimento',
      descricao: 'Desenvolva novas estratégias e impulsione o crescimento da empresa',
      status: 'ativa',
      dataInicio: '2024-02-15',
      dataFim: '2024-05-31',
      imagem: 'https://readdy.ai/api/search-image?query=innovation%20growth%20campaign%20creative%20ideas%20lightbulb%20technology%20professional%20business%20background%20with%20futuristic%20elements%20and%20progress%20indicators&width=800&height=400&seq=camp5&orientation=landscape',
      progresso: 35,
      pontosGanhos: 1750,
      pontosDistribuidos: 5000,
      pontosDisponiveis: 3250,
      metaDefinida: 5000,
      ranking: 12,
      totalParticipantes: 28,
      recursos: {
        niveis: true,
        distintivos: false,
        missoes: true,
        jogos: false
      },
      regulamento: `REGULAMENTO DA CAMPANHA INOVAÇÃO E CRESCIMENTO

1. OBJETIVO
Estimular o desenvolvimento de novas estratégias e iniciativas que impulsionem o crescimento da empresa.

2. PERÍODO DE VIGÊNCIA
15/02/2024 a 31/05/2024.

3. PARTICIPANTES ELEGÍVEIS
Todos os colaboradores de todas as áreas da empresa.

4. MECÂNICA
Pontos são acumulados por propostas de melhoria aprovadas, missões concluídas e metas de crescimento atingidas.

5. PONTUAÇÃO
- Proposta de melhoria aprovada: 500 a 2.000 pontos
- Implementação de melhoria: bônus de 1.000 pontos
- Missões: conforme tabela de missões

6. PREMIAÇÃO
As 3 melhores propostas implementadas recebem prêmios especiais. Todos os participantes com propostas aprovadas recebem reconhecimento público.

7. REGRAS GERAIS
Propostas devem ser originais e viáveis. A comissão avaliadora tem até 15 dias para analisar cada proposta submetida.`,
      faq: [
        { pergunta: 'Como submeto uma proposta de melhoria?', resposta: 'Acesse a área de missões e procure pela missão "Proposta de Inovação". Preencha o formulário com sua ideia, impacto esperado e como implementá-la.' },
        { pergunta: 'Quem avalia as propostas?', resposta: 'Uma comissão formada por gestores de diferentes áreas avalia as propostas em até 15 dias úteis após a submissão.' },
        { pergunta: 'Posso submeter mais de uma proposta?', resposta: 'Sim! Não há limite de propostas por participante. Cada proposta aprovada gera pontuação independente.' },
        { pergunta: 'Por que não há distintivos nesta campanha?', resposta: 'Esta campanha é focada em inovação e crescimento coletivo, sem sistema de distintivos individuais para incentivar a colaboração em equipe.' },
        { pergunta: 'O que acontece se minha proposta for rejeitada?', resposta: 'Propostas rejeitadas podem ser revisadas e resubmetidas uma vez, incorporando o feedback da comissão avaliadora.' },
      ],
      progressao: {
        campanhaId: '5',
        xpAtual: 850,
        nivelAtual: calcularNivelAtual(850, niveisBase),
        proximoNivel: calcularProximoNivel(calcularNivelAtual(850, niveisBase), niveisBase),
        progressoNivel: calcularProgressoNivel(850, calcularNivelAtual(850, niveisBase), calcularProximoNivel(calcularNivelAtual(850, niveisBase), niveisBase)),
        distintivosConquistados: [],
        totalDistintivos: distintivosCompletos.length,
        missoesCompletas: 3,
        totalMissoes: 10,
        ranking: 12,
        totalParticipantes: 28,
        pontosDisponiveis: 3250,
        jogosDisponiveis: [],
        missoesAtivas: missoesCompletas.slice(0, 6),
        recompensasDisponiveis: recompensasCompletas.slice(0, 4),
        rankingCompleto: gerarRanking(28, 12)
      }
    }
  ]);

  const [participante] = useState<Participante>({
    id: '1',
    nome: 'Carlos Silva',
    email: 'carlos.silva@empresa.com',
    cpf: '123.456.789-00',
    foto: 'https://i.pravatar.cc/150?img=12',
    cargo: 'Consultor de Vendas',
    departamento: 'Comercial',
    pontosTotal: 10030,
    pontosDisponiveis: 8450,
    xpTotal: 11500,
    xpAtual: 3200,
    campanhasAtivas: 3,
    missoesCompletas: 24,
    resgatesRealizados: 8,
    distintivosConquistados: 2,
    notificacoesNaoLidas: 5
  });

  const [campanhaAtual, setCampanhaAtual] = useState<Campanha | null>(campanhas[0]);

  const atualizarParticipante = () => {
    // Função para atualizar dados do participante
    console.log('Atualizando dados do participante...');
  };

  const confirmarParticipacao = (campanhaId: string) => {
    setCampanhas(prev => prev.map(c => {
      if (c.id !== campanhaId) return c;
      return {
        ...c,
        status: 'ativa' as const,
        progresso: 0,
        ranking: c.totalParticipantes + 1,
        totalParticipantes: c.totalParticipantes + 1,
        progressao: {
          ...c.progressao,
          ranking: c.totalParticipantes + 1,
          totalParticipantes: c.totalParticipantes + 1,
          missoesAtivas: missoesCompletas,
          rankingCompleto: gerarRanking(c.totalParticipantes + 1, c.totalParticipantes + 1),
        }
      };
    }));
  };

  return (
    <ParticipanteContext.Provider
      value={{
        participante,
        campanhas,
        campanhaAtual,
        setCampanhaAtual,
        atualizarParticipante,
        confirmarParticipacao,
        todosDistintivos: distintivosCompletos
      }}
    >
      {children}
    </ParticipanteContext.Provider>
  );
}

export function useParticipante() {
  const context = useContext(ParticipanteContext);
  if (context === undefined) {
    throw new Error('useParticipante must be used within a ParticipanteProvider');
  }
  return context;
}